import {
  createApiErrorFromResponse,
  readApiErrorDetail,
} from "@airange/shared/api";
import { EXAM_ADMIN_PAGE_PATHS } from "@airange/shared/exam-routes";
import {
  createOidcClientIdResolver,
  createSsoSessionCoordinator,
  logoutAndClearLocalSession,
  parseJsonResponse,
  type SsoSessionApiClient,
} from "@airange/shared/oidc-session";
import { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import {
  bindCenterOpenApiFetch,
  normalizeCenterOpenApiBaseUrl,
} from "@airange/shared/center-openapi-transport";
import { createOidcBrowserClient } from "@airange/shared/oidc";
import type { UserSession as SsoUserSession } from "./auth-contract";
import { SSO_API_BASE_URL, SSO_OIDC_SCOPE } from "./env";
import {
  clearSession,
  clearStoredAuthSource,
  getAccessToken,
  getStoredAuthSource,
  setAccessToken,
  setSession,
  setStoredAuthSource,
} from "./session";
import { sanitizeRedirectPath } from "./sso-redirect";

export type OidcCallbackCompleteResult = {
  redirectPath: string;
  user: SsoUserSession;
};

export type StartSsoOidcAuthorizationOptions = {
  prompt?: "none" | "login";
};

export { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";

const SSO_OIDC_APP_KEY = "exam-admin";
const OIDC_PENDING_REDIRECT_PATH_KEY = "exam.admin.oidc.pending_redirect_path";
const SSO_COOKIE_SESSION_MARKER = "exam-sso-cookie-session";
const CENTER_OPENAPI_SERVICE_ROOT_OPTIONS = { mode: "service-root" } as const;

const oidcClientIdResolver = createOidcClientIdResolver({
  appKey: SSO_OIDC_APP_KEY,
  ssoApiBaseUrl: SSO_API_BASE_URL,
});

const oidcClient = createOidcBrowserClient({
  authority: SSO_API_BASE_URL,
  callbackPath: OIDC_CALLBACK_PATH,
  defaultRedirectPath: EXAM_ADMIN_PAGE_PATHS.dashboard,
  pendingRedirectPathKey: OIDC_PENDING_REDIRECT_PATH_KEY,
  resolveClientId: oidcClientIdResolver.resolveClientId,
  sanitizeRedirectPath: (value) => sanitizeRedirectPath(value, EXAM_ADMIN_PAGE_PATHS.dashboard),
  scope: SSO_OIDC_SCOPE,
  stateStorePrefix: "exam.admin.oidc.state.",
  userStorePrefix: "exam.admin.oidc.user.",
});

async function requestExamAdminCurrentUser(fetchImpl: typeof fetch, accessToken: string | null) {
  const response = await fetchImpl(`${SSO_API_BASE_URL}/api/auth/me`, {
    credentials: "include",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
    method: "GET",
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  return await parseJsonResponse<SsoUserSession>(response);
}

function setSsoAccessToken(accessToken: string, _user: SsoUserSession) {
  setAccessToken(accessToken);
}

function getSsoBearerAccessToken() {
  const accessToken = getAccessToken();
  return accessToken === SSO_COOKIE_SESSION_MARKER ? null : accessToken;
}

const ssoSessionCoordinator = createSsoSessionCoordinator<SsoUserSession, "sso">({
  allowCookieBootstrap: true,
  authSourceValue: "sso",
  canBootstrap: () => getStoredAuthSource() === "sso",
  canRefresh: () => getStoredAuthSource() === "sso",
  clearAuthSource: clearStoredAuthSource,
  clearSession,
  getAccessToken: getSsoBearerAccessToken,
  requestCurrentUser: requestExamAdminCurrentUser,
  resolveClientId: oidcClientIdResolver.resolveClientId,
  setAccessToken,
  setAuthSource: setStoredAuthSource,
  setSession: setSsoAccessToken,
  ssoApiBaseUrl: SSO_API_BASE_URL,
});

export const readErrorDetail = readApiErrorDetail;

export function consumeSsoOidcAuthorizationRedirectPath(
  defaultPath = EXAM_ADMIN_PAGE_PATHS.dashboard,
) {
  return oidcClient.consumePendingRedirectPath(defaultPath);
}

export async function startSsoOidcAuthorization(
  redirectPath: string,
  options: StartSsoOidcAuthorizationOptions = {},
) {
  if (typeof window === "undefined") {
    return;
  }
  await oidcClient.startAuthorization(redirectPath, {
    prompt: options.prompt,
  });
}

export async function completeSsoOidcCallback(
  _code: string,
  _state: string,
  fetchImpl: typeof fetch = fetch,
): Promise<OidcCallbackCompleteResult> {
  return ssoSessionCoordinator.completeOidcCallback(
    (callbackFetchImpl) => oidcClient.completeCallback(callbackFetchImpl),
    fetchImpl,
  );
}

export async function refreshSsoCenterAccessToken(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  return ssoSessionCoordinator.refreshAccessToken(fetchImpl);
}

export async function logoutSsoSession(fetchImpl: typeof fetch = fetch) {
  await logoutAndClearLocalSession(
    {
      clearLocalSession: () => {
        clearSession();
        clearStoredAuthSource();
      },
      getAccessToken,
      ssoApiBaseUrl: SSO_API_BASE_URL,
    },
    fetchImpl,
  );
}

export function createCenterApiClient(
  baseUrl: string,
  fetchImpl: typeof fetch = fetch,
): SsoSessionApiClient {
  return ssoSessionCoordinator.createApiClient(
    normalizeCenterOpenApiBaseUrl(baseUrl, CENTER_OPENAPI_SERVICE_ROOT_OPTIONS),
    fetchImpl,
  );
}

export function getCenterOpenApiClientServiceRootUrl(baseUrl: string) {
  return normalizeCenterOpenApiBaseUrl(baseUrl, CENTER_OPENAPI_SERVICE_ROOT_OPTIONS);
}

export const createCenterOpenApiFetch = bindCenterOpenApiFetch(
  createCenterApiClient,
  CENTER_OPENAPI_SERVICE_ROOT_OPTIONS,
);
